import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { projectsApi, suppliersApi, collectionsApi, reportsApi, projectGroupsApi, respondentsApi, qualityQuestionsApi } from '@/services/api';
import { Project, QualityQuestion } from '@/types';
import EditProjectForm from '@/components/EditProjectForm';
import ResearchDefenderModal from '@/components/ResearchDefenderModal';
import ProjectAnalytics from '@/components/ProjectAnalytics';
import ReconciliationSection from '@/components/ReconciliationSection';
import CollapsibleCard from '@/components/CollapsibleCard';
import ExportMenu from '@/components/ExportMenu';
import { ArrowLeft, Search, BarChart3, FileText, Check, X, AlertTriangle, Shield, CheckCircle, Clock, Globe, User, Monitor, ClipboardList, Building, Target, Trash2, GripVertical, Download } from 'lucide-react';
import { COUNTRIES, getCountryName } from '@/constants/countries';
import { formatDate, formatDateTime } from '@/utils/dateFormat';

// Effective RD settings for a collection = collection override ?? supplier
// default ?? built-in default (matches the collection edit form). A collection
// is "fully protected" (green dot) when all three are on. Mirrors the
// server-side rollup used on the projects index.
type RdLike = { rdEnabled?: boolean | null; rdBlockDuplicates?: boolean | null; enableGeoBlocker?: boolean | null };
const collectionRdSettings = (collection: RdLike, supplier?: RdLike) => {
  const rdEnabled = Boolean(collection.rdEnabled ?? supplier?.rdEnabled ?? true);
  const duplicates = Boolean(collection.rdBlockDuplicates ?? supplier?.rdBlockDuplicates ?? true);
  const geo = Boolean(collection.enableGeoBlocker ?? supplier?.enableGeoBlocker ?? false);
  return { rdEnabled, duplicates, geo, green: rdEnabled && duplicates && geo };
};

type CollectionSortField = 'name' | 'createdAt' | 'country' | 'provider' | 'updatedAt';
type CollectionSortDirection = 'asc' | 'desc';

const COLLECTION_SORT_FIELDS: { value: CollectionSortField; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'createdAt', label: 'Date added' },
  { value: 'country', label: 'Country' },
  { value: 'provider', label: 'Provider' },
  { value: 'updatedAt', label: 'Last updated' },
];

const collectionOrderKey = (ids: string[]) => ids.join(',');

const sortCollectionsList = (
  items: any[],
  field: CollectionSortField,
  direction: CollectionSortDirection,
  getProviderName: (collection: any) => string,
) => {
  return [...items].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'name':
        cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        break;
      case 'country':
        cmp = a.country.localeCompare(b.country, undefined, { sensitivity: 'base' });
        break;
      case 'provider':
        cmp = getProviderName(a).localeCompare(getProviderName(b), undefined, { sensitivity: 'base' });
        break;
      case 'createdAt':
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'updatedAt':
        cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
    }
    return direction === 'asc' ? cmp : -cmp;
  });
};

const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [error, setError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  // Bumped after a project save so ProjectAnalytics re-fetches (e.g. updated total target)
  const [analyticsRefreshKey, setAnalyticsRefreshKey] = useState(0);
  const [editError, setEditError] = useState<string>('');
  const [currentDomain] = useState(window.location.origin);
  const [copiedButton, setCopiedButton] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
  const [showResearchDefenderModal, setShowResearchDefenderModal] = useState(false);
  const [selectedResearchDefenderData, setSelectedResearchDefenderData] = useState<any>(null);
  const [selectedResearchDefenderId, setSelectedResearchDefenderId] = useState<string>('');
  const [selectedRdBlockReason, setSelectedRdBlockReason] = useState<string | null>(null);
  const [selectedRdBlockType, setSelectedRdBlockType] = useState<string | null>(null);
  const [selectedBrowserFingerprint, setSelectedBrowserFingerprint] = useState<any>(null);
  const [editingCollection, setEditingCollection] = useState<any>(null);
  const [displayCollections, setDisplayCollections] = useState<any[]>([]);
  const [sortField, setSortField] = useState<CollectionSortField>('name');
  const [sortDirection, setSortDirection] = useState<CollectionSortDirection>('asc');
  const [draggedCollectionId, setDraggedCollectionId] = useState<string | null>(null);
  const [dragOverCollectionId, setDragOverCollectionId] = useState<string | null>(null);
  const [screenerError, setScreenerError] = useState('');
  const [screenerSaving, setScreenerSaving] = useState(false);
  const [selectedScreenerIds, setSelectedScreenerIds] = useState<string[]>([]);
  const [screenerDirty, setScreenerDirty] = useState(false);

  const urls = [
    { title: 'Complete', path: 'complete' },
    { title: 'Terminate', path: 'terminate' },
    { title: 'Quota', path: 'quota' },
    { title: 'Security', path: 'security' }
  ];

  const queryClient = useQueryClient();

  // Respondent deletion (test-data cleanup from the Activity Log) is available to
  // any logged-in user; the DELETE /api/respondents/:id route is auth-only. This
  // page already requires authentication, so the control is always shown.
  const canDeleteRespondents = true;

  const { data: response, isLoading, error: queryError } = useQuery(
    ['project', id],
    () => projectsApi.getById(id!),
    {
      enabled: !!id,
      onError: (error) => {
        setError('Failed to load project details');
        console.error('Project query error:', error);
      },
      onSuccess: (data) => {
        // Project data loaded successfully
      },
    }
  );

  // Fetch suppliers data (high limit so collection dropdown has URL templates for all)
  const { data: suppliersResponse } = useQuery(
    'suppliers',
    () => suppliersApi.getAll({ limit: 100 }),
    {
      onError: (error) => {
        console.error('Suppliers query error:', error);
      },
    }
  );

  // Fetch collections for this project
  const { data: collectionsResponse, isLoading: collectionsLoading } = useQuery(
    ['project-collections', id],
    () => collectionsApi.getByProject(id!),
    {
      enabled: !!id,
      onError: (error) => {
        console.error('Collections query error:', error);
      },
    }
  );

  // Quality screener question bank + project assignments
  const { data: qualityBankResponse } = useQuery(
    ['quality-questions', 'active'],
    () => qualityQuestionsApi.getAll({ isActive: true }),
    {
      onError: (err) => console.error('Quality questions query error:', err),
    }
  );

  const { data: projectScreenerResponse, isLoading: projectScreenerLoading } = useQuery(
    ['project-quality-questions', id],
    () => qualityQuestionsApi.getProjectAssignments(id!),
    {
      enabled: !!id,
      onError: (err) => console.error('Project screener query error:', err),
    }
  );

  const deleteProjectMutation = useMutation(projectsApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('projects');
      navigate('/admin/projects');
    },
  });

  const duplicateProjectMutation = useMutation(projectsApi.duplicate, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('projects');
      // Navigate to the duplicated project
      if (data.data?.id) {
        navigate(`/admin/projects/${data.data.id}`);
      }
    },
  });

  const project = response?.data as Project;
  const suppliers = suppliersResponse?.data || [];
  const collections = collectionsResponse?.data || [];
  const qualityBank: QualityQuestion[] = qualityBankResponse?.data || [];
  const assignedScreenerQuestions: QualityQuestion[] = projectScreenerResponse?.data || [];

  // Sync selected IDs from server when not editing locally
  useEffect(() => {
    if (!screenerDirty && projectScreenerResponse?.data) {
      setSelectedScreenerIds(projectScreenerResponse.data.map((q: QualityQuestion) => q.id));
    }
  }, [projectScreenerResponse, screenerDirty]);

  const handleSaveProjectScreener = async () => {
    if (!id) return;
    setScreenerSaving(true);
    setScreenerError('');
    try {
      const result = await qualityQuestionsApi.setProjectAssignments(id, selectedScreenerIds);
      if (result.success) {
        setScreenerDirty(false);
        queryClient.invalidateQueries(['project-quality-questions', id]);
      } else {
        setScreenerError(result.error || 'Failed to save quality screener questions');
      }
    } catch (err: any) {
      setScreenerError(err.response?.data?.error || 'Failed to save quality screener questions');
    } finally {
      setScreenerSaving(false);
    }
  };

  const toggleScreenerQuestion = (questionId: string) => {
    setScreenerDirty(true);
    setSelectedScreenerIds((prev) => {
      if (prev.includes(questionId)) {
        return prev.filter((id) => id !== questionId);
      }
      return [...prev, questionId];
    });
  };

  const reorderScreenerQuestion = (draggedId: string, targetId: string) => {
    setScreenerDirty(true);
    setSelectedScreenerIds((prev) => {
      const draggedIndex = prev.indexOf(draggedId);
      const targetIndex = prev.indexOf(targetId);
      if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return prev;
      const next = [...prev];
      const [removed] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, removed);
      return next;
    });
  };

  const serverOrderKey = useMemo(
    () => collectionOrderKey((collectionsResponse?.data || []).map((c: any) => c.id)),
    [collectionsResponse?.data],
  );

  const isCollectionOrderDirty = useMemo(
    () =>
      displayCollections.length > 0 &&
      collectionOrderKey(displayCollections.map((c) => c.id)) !== serverOrderKey,
    [displayCollections, serverOrderKey],
  );

  // Sync from server only when the saved order changes — never wipe unsaved local order.
  useEffect(() => {
    if (!collectionsResponse?.data) {
      setDisplayCollections([]);
      return;
    }
    setDisplayCollections((prev) => {
      if (
        prev.length > 0 &&
        collectionOrderKey(prev.map((c) => c.id)) !== serverOrderKey
      ) {
        return prev;
      }
      return collectionsResponse.data;
    });
  }, [collectionsResponse?.data, serverOrderKey]);

  const getProviderNameForSort = (collection: any) =>
    suppliers.find((s: any) => s.id === collection.supplierId)?.name ?? '';

  const applyCollectionSort = (field: CollectionSortField, direction: CollectionSortDirection) => {
    setSortField(field);
    setSortDirection(direction);
    setDisplayCollections((prev) =>
      sortCollectionsList(prev, field, direction, getProviderNameForSort),
    );
  };

  const handleCollectionDragStart = (collectionId: string) => {
    setDraggedCollectionId(collectionId);
  };

  const handleCollectionDragOver = (event: React.DragEvent, overId: string) => {
    event.preventDefault();
    if (draggedCollectionId && draggedCollectionId !== overId) {
      setDragOverCollectionId(overId);
    }
  };

  const handleCollectionDrop = (event: React.DragEvent, targetId: string) => {
    event.preventDefault();
    if (!draggedCollectionId || draggedCollectionId === targetId) {
      setDraggedCollectionId(null);
      setDragOverCollectionId(null);
      return;
    }

    setDisplayCollections((prev) => {
      const draggedIndex = prev.findIndex((c) => c.id === draggedCollectionId);
      const targetIndex = prev.findIndex((c) => c.id === targetId);
      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const next = [...prev];
      const [removed] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, removed);
      return next;
    });
    setDraggedCollectionId(null);
    setDragOverCollectionId(null);
  };

  const handleCollectionDragEnd = () => {
    setDraggedCollectionId(null);
    setDragOverCollectionId(null);
  };

  // Helper function to get collection name from collectionId
  const getCollectionName = (collectionId: string) => {
    if (!collectionId) return '-';
    const collection = collections.find((c: any) => c.id === collectionId);
    if (!collection) return '-';
    return collection.name;
  };

  // Helper function to get supplier name from collectionId
  const getSupplierName = (collectionId: string) => {
    if (!collectionId) return '-';
    const collection = collections.find((c: any) => c.id === collectionId);
    if (!collection || !collection.supplierId) return '-';
    const supplier = suppliers.find((s: any) => s.id === collection.supplierId);
    return supplier ? supplier.name : '-';
  };

  // Helper function to get country from collectionId
  const getCountry = (collectionId: string) => {
    if (!collectionId) return '-';
    const collection = collections.find((c: any) => c.id === collectionId);
    if (!collection) return '-';
    return collection.country || '-';
  };

  // Helper function to get RD-detected location
  const getRDLocation = (respondent: any) => {
    if (!respondent.researchDefenderData) return '-';
    try {
      const rdData = JSON.parse(respondent.researchDefenderData);
      if (rdData?.Respondent?.country_code) {
        return rdData.Respondent.country_code;
      }
      return '-';
    } catch {
      return '-';
    }
  };

  // State for respondent pagination
  const [respondentsOffset, setRespondentsOffset] = useState(0);
  const [allRespondents, setAllRespondents] = useState<any[]>([]);
  const [hasMoreRespondents, setHasMoreRespondents] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Activity Log view: market / supplier / collection / respondent
  const [activityView, setActivityView] = useState<'market' | 'supplier' | 'collection' | 'respondent'>('market');

  // Masked ID search for the Activity Log. The search lives in a modal opened
  // from a subtle magnifying-glass button. `maskedIdSearch` is the live input
  // value; `appliedSearch` is the term actually sent to the server (on submit).
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [maskedIdSearch, setMaskedIdSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  // Fetch respondents activity data (now that we have the project)
  const { data: respondentsResponse, isLoading: respondentsLoading, refetch: refetchRespondents } = useQuery(
    ['project-respondents', id, respondentsOffset, appliedSearch],
    () => projectsApi.getRespondents(id!, { limit: 10, offset: respondentsOffset, search: appliedSearch || undefined }),
    {
      enabled: !!id,
      staleTime: 0,
      cacheTime: 30000,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      onSuccess: (data) => {
        if (respondentsOffset === 0) {
          setAllRespondents(data.data?.respondents || []);
        } else {
          setAllRespondents(prev => [...prev, ...(data.data?.respondents || [])]);
        }
        setHasMoreRespondents(data.data?.hasMore || false);
      },
      onError: (error) => {
        console.error('Respondents query error:', error);
      },
      refetchInterval: respondentsOffset === 0 ? 30000 : false,
    }
  );

  // CR-04: per-supplier breakdown for this project
  const { data: supplierSummaryResponse } = useQuery(
    ['project-supplier-summary', id],
    () => projectsApi.getSupplierSummary(id!),
    {
      enabled: !!id,
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Supplier summary query error:', error);
      },
    }
  );
  // CR-03: project groups — to show this project's group name
  const { data: groupsResponse } = useQuery(
    'project-groups',
    () => projectGroupsApi.getAll(),
    { onError: (error) => console.error('Project groups query error:', error) }
  );
  const projectGroups = (groupsResponse?.data || []) as Array<{ id: string; name: string }>;

  const supplierBreakdown = (supplierSummaryResponse?.data?.perSupplier ?? []) as Array<{
    supplierId: string;
    name: string;
    completes: number;
    terminates: number;
    quotaFull: number;
    security: number;
    quality: number;
    ir: number;
    meanLoiSeconds: number;
    medianLoiSeconds: number;
  }>;
  const supplierTotals = supplierSummaryResponse?.data?.totals as
    | {
        completes: number;
        terminates: number;
        quotaFull: number;
        security: number;
        quality: number;
        ir: number;
        meanLoiSeconds: number;
        medianLoiSeconds: number;
      }
    | undefined;

  // CR-08: per-collection breakdown for this project
  const { data: collectionSummaryResponse } = useQuery(
    ['project-collection-summary', id],
    () => projectsApi.getCollectionSummary(id!),
    {
      enabled: !!id,
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Collection summary query error:', error);
      },
    }
  );

  const collectionBreakdown = (collectionSummaryResponse?.data?.perCollection ?? []) as Array<{
    collectionId: string;
    name: string;
    country: string;
    supplierName: string;
    completes: number;
    terminates: number;
    quotaFull: number;
    security: number;
    quality: number;
    ir: number;
    meanLoiSeconds: number;
    medianLoiSeconds: number;
  }>;
  const collectionTotals = collectionSummaryResponse?.data?.totals as
    | {
        completes: number;
        terminates: number;
        quotaFull: number;
        security: number;
        quality: number;
        ir: number;
        meanLoiSeconds: number;
        medianLoiSeconds: number;
      }
    | undefined;

  // Per-market (country) breakdown for this project
  const { data: marketSummaryResponse } = useQuery(
    ['project-market-summary', id],
    () => projectsApi.getMarketSummary(id!),
    {
      enabled: !!id,
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Market summary query error:', error);
      },
    }
  );

  const marketBreakdown = ([...(marketSummaryResponse?.data?.perMarket ?? [])] as Array<{
    country: string;
    completes: number;
    terminates: number;
    quotaFull: number;
    security: number;
    quality: number;
    ir: number;
    meanLoiSeconds: number;
    medianLoiSeconds: number;
  }>).sort((a, b) => {
    const labelA = a.country ? getCountryName(a.country) : 'Unknown';
    const labelB = b.country ? getCountryName(b.country) : 'Unknown';
    return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
  });
  const marketTotals = marketSummaryResponse?.data?.totals as
    | {
        completes: number;
        terminates: number;
        quotaFull: number;
        security: number;
        quality: number;
        ir: number;
        meanLoiSeconds: number;
        medianLoiSeconds: number;
      }
    | undefined;

  // Fetch dropout logs for this project
  const { data: dropoutResponse, isLoading: dropoutLoading } = useQuery(
    ['project-dropouts', project?.projectId],
    async () => {
      if (!project?.projectId) return null;

      try {
        const token = localStorage.getItem('token');
        if (!token) return null;

        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/dropout/logs?projectId=${project.projectId}&limit=10`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.warn('Dropout API response not ok:', response.status);
          return null;
        }

        return await response.json();
      } catch (error) {
        console.warn('Dropout query failed:', error);
        return null;
      }
    },
    {
      enabled: !!project?.projectId && !!localStorage.getItem('token'),
      retry: false,
      refetchInterval: 30000,
    }
  );

  // Collection mutations
  const deleteCollectionMutation = useMutation(collectionsApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries(['project-collections', id]);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to delete collection');
    },
  });

  const saveCollectionOrderMutation = useMutation(
    ({ projectId, orderedIds }: { projectId: string; orderedIds: string[] }) =>
      collectionsApi.reorder(projectId, orderedIds),
    {
      onSuccess: (result) => {
        setError('');
        if (result?.data) {
          setDisplayCollections(result.data);
          queryClient.setQueryData(['project-collections', id], result);
        } else {
          queryClient.invalidateQueries(['project-collections', id]);
        }
        // Activity Log "By Collection" uses the same saved sortOrder.
        queryClient.invalidateQueries(['project-collection-summary', id]);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.error ||
          (error.message === 'Network Error'
            ? 'Could not reach the server. Check that the API is running and try again.'
            : 'Failed to save collection order');
        setError(message);
      },
    },
  );

  const handleSaveCollectionOrder = () => {
    if (!id || !isCollectionOrderDirty) return;
    setError('');
    saveCollectionOrderMutation.mutate({
      projectId: id,
      orderedIds: displayCollections.map((c) => c.id),
    });
  };

  // Respondent deletion (test-data cleanup from the Activity Log). Project
  // statistics — supplier/collection summaries, IR, LOI — are computed live
  // from the respondents table server-side, so deleting a row and refetching
  // those queries re-runs every affected calculation.
  const deleteRespondentMutation = useMutation(
    (respondentId: string) => respondentsApi.delete(respondentId),
    {
      onSuccess: (_data, respondentId) => {
        // Optimistically drop the row so the table updates immediately,
        // regardless of how many "load more" pages are currently shown.
        setAllRespondents(prev => prev.filter((r: any) => r.id !== respondentId));
        // Re-run the respondent-derived calculations.
        queryClient.invalidateQueries(['project-supplier-summary', id]);
        queryClient.invalidateQueries(['project-collection-summary', id]);
        queryClient.invalidateQueries(['project-market-summary', id]);
        queryClient.invalidateQueries(['project', id]);
      },
      onError: (error: any) => {
        setError(error.response?.data?.error || 'Failed to delete respondent');
      },
    }
  );

  const handleDeleteRespondent = (respondent: any) => {
    const label = respondent.maskedId || respondent.panelId || 'this respondent';
    if (
      window.confirm(
        `Delete test data for ${label}?\n\nThis permanently removes the respondent and re-runs the project statistics. This action cannot be undone.`
      )
    ) {
      deleteRespondentMutation.mutate(respondent.id);
    }
  };

  // getCountryName is now imported from @/constants/countries

  const handleDeleteProject = () => {
    if (window.confirm(`Are you sure you want to delete "${project?.name}"? This action cannot be undone.`)) {
      deleteProjectMutation.mutate(project!.id);
    }
  };

  const handleDuplicateProject = () => {
    if (window.confirm(`Create a copy of "${project?.name}"? Collections will be copied but respondent data will not.`)) {
      duplicateProjectMutation.mutate(project!.id);
    }
  };

  const handleDeleteCollection = (collectionId: string, collectionName: string) => {
    if (window.confirm(`Are you sure you want to delete "${collectionName}"? This action cannot be undone.`)) {
      deleteCollectionMutation.mutate(collectionId);
    }
  };

  const handleEditProject = () => {
    setEditError('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditError('');
    setIsEditing(false);
  };

  const handleSearchRespondents = () => {
    setRespondentsOffset(0);
    setAppliedSearch(maskedIdSearch.trim());
    setShowSearchModal(false);
  };

  const handleClearRespondentSearch = () => {
    setMaskedIdSearch('');
    setRespondentsOffset(0);
    setAppliedSearch('');
    setShowSearchModal(false);
  };

  const handleOpenSearchModal = () => {
    setMaskedIdSearch(appliedSearch);
    setShowSearchModal(true);
  };

  const handleLoadMoreRespondents = async () => {
    if (!hasMoreRespondents || isLoadingMore) return;

    setIsLoadingMore(true);
    setRespondentsOffset(prev => prev + 10);

    try {
      await refetchRespondents();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleDownloadRespondents = async (
    format: 'csv' | 'txt',
    options: {
      statusFilter?: 'all' | 'completed';
      supplierId?: string;
      supplierName?: string;
      collectionId?: string;
      collectionName?: string;
      country?: string;
      marketName?: string;
    } = {}
  ) => {
    try {
      const {
        statusFilter = 'all',
        supplierId,
        supplierName,
        collectionId,
        collectionName,
        country,
        marketName,
      } = options;
      const blob = await reportsApi.exportProjectData({
        projectId: id!,
        format,
        ...(statusFilter !== 'all' ? { statusFilter } : {}),
        ...(supplierId ? { supplierId } : {}),
        ...(collectionId ? { collectionId } : {}),
        ...('country' in options ? { country: country ?? '' } : {}),
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const slug = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const projectName = project?.name ? slug(project.name) : 'project';
      const scopePart = supplierName
        ? `-${slug(supplierName)}`
        : collectionName
          ? `-${slug(collectionName)}`
          : marketName
            ? `-${slug(marketName)}`
            : '';
      const statusPart = statusFilter === 'completed' ? '-completes' : '';
      const date = new Date().toISOString().split('T')[0];
      link.download = `${projectName}${scopePart}${statusPart}-export-${date}.${format}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  // CR-05: all-suppliers export = the aggregated per-supplier summary table
  // (not raw respondent rows). Built client-side from the data already loaded.
  const handleDownloadSupplierSummary = () => {
    try {
      const headers = ['Supplier', 'Completes', 'Terminates', 'Quota Full', 'Security', 'Quality', 'IR (%)', 'Mean LOI', 'Median LOI'];
      const esc = (v: any) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows: (string | number)[][] = supplierBreakdown.map(r => [
        r.name, r.completes, r.terminates, r.quotaFull, r.security, r.quality ?? 0, r.ir,
        formatLoiHHMMSS(r.meanLoiSeconds), formatLoiHHMMSS(r.medianLoiSeconds),
      ]);
      if (supplierTotals) {
        rows.push([
          'Total', supplierTotals.completes, supplierTotals.terminates, supplierTotals.quotaFull,
          supplierTotals.security, supplierTotals.quality ?? 0, supplierTotals.ir,
          formatLoiHHMMSS(supplierTotals.meanLoiSeconds), formatLoiHHMMSS(supplierTotals.medianLoiSeconds),
        ]);
      }
      const csv = [headers, ...rows].map(row => row.map(esc).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const slug = (s: string) => s.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const projectName = project?.name ? slug(project.name) : 'project';
      const date = new Date().toISOString().split('T')[0];
      link.download = `${projectName}-supplier-summary-${date}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Summary download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  // CR-08: all-collections export = the aggregated per-collection summary table
  // (not raw respondent rows). Built client-side from the data already loaded.
  const handleDownloadCollectionSummary = () => {
    try {
      const headers = ['Collection', 'Country', 'Supplier', 'Completes', 'Terminates', 'Quota Full', 'Security', 'Quality', 'IR (%)', 'Mean LOI', 'Median LOI'];
      const esc = (v: any) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows: (string | number)[][] = collectionBreakdown.map(r => [
        r.name, r.country, r.supplierName, r.completes, r.terminates, r.quotaFull, r.security, r.quality ?? 0, r.ir,
        formatLoiHHMMSS(r.meanLoiSeconds), formatLoiHHMMSS(r.medianLoiSeconds),
      ]);
      if (collectionTotals) {
        rows.push([
          'Total', '', '', collectionTotals.completes, collectionTotals.terminates, collectionTotals.quotaFull,
          collectionTotals.security, collectionTotals.quality ?? 0, collectionTotals.ir,
          formatLoiHHMMSS(collectionTotals.meanLoiSeconds), formatLoiHHMMSS(collectionTotals.medianLoiSeconds),
        ]);
      }
      const csv = [headers, ...rows].map(row => row.map(esc).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const slug = (s: string) => s.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const projectName = project?.name ? slug(project.name) : 'project';
      const date = new Date().toISOString().split('T')[0];
      link.download = `${projectName}-collection-summary-${date}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Summary download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  // All-markets export = the aggregated per-market summary table
  // (not raw respondent rows). Built client-side from the data already loaded.
  const handleDownloadMarketSummary = () => {
    try {
      const headers = ['Market', 'Completes', 'Terminates', 'Quota Full', 'Security', 'Quality', 'IR (%)', 'Mean LOI', 'Median LOI'];
      const esc = (v: any) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const marketLabel = (code: string) => (code ? getCountryName(code) : 'Unknown');
      const rows: (string | number)[][] = marketBreakdown.map(r => [
        marketLabel(r.country), r.completes, r.terminates, r.quotaFull, r.security, r.quality ?? 0, r.ir,
        formatLoiHHMMSS(r.meanLoiSeconds), formatLoiHHMMSS(r.medianLoiSeconds),
      ]);
      if (marketTotals) {
        rows.push([
          'Total', marketTotals.completes, marketTotals.terminates, marketTotals.quotaFull,
          marketTotals.security, marketTotals.quality ?? 0, marketTotals.ir,
          formatLoiHHMMSS(marketTotals.meanLoiSeconds), formatLoiHHMMSS(marketTotals.medianLoiSeconds),
        ]);
      }
      const csv = [headers, ...rows].map(row => row.map(esc).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const slug = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const projectName = project?.name ? slug(project.name) : 'project';
      const date = new Date().toISOString().split('T')[0];
      link.download = `${projectName}-market-summary-${date}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Summary download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  const handleDownloadScreenerExport = async (options: { collectionId?: string; collectionName?: string } = {}) => {
    if (!id) return;
    try {
      const { collectionId, collectionName } = options;
      const blob = await reportsApi.exportScreenerData({
        projectId: id,
        ...(collectionId ? { collectionId } : {}),
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const slug = (s: string) => s.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const projectName = project?.name ? slug(project.name) : 'project';
      const collectionPart = collectionName ? `-${slug(collectionName)}` : '';
      const date = new Date().toISOString().split('T')[0];
      link.download = `${projectName}${collectionPart}-screener-export-${date}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Screener export failed:', error);
      alert('Screener export failed. Please try again.');
    }
  };

  const getSupplierLink = (collection: any) => {
    const selectedSupplier = suppliers.find(s => s.id === collection.supplierId);
    const placeholderToken = selectedSupplier?.placeholderToken || 'XXXX';

    const uniqueId = `RC|${collection.id}|${collection.country}`;
    const encodedId = btoa(uniqueId).replace(/[^a-zA-Z0-9]/g, '');
    return `${window.location.origin}/survey?collection=${encodedId}&rc=${placeholderToken}`;
  };

  const decodeCollectionId = (encodedId: string) => {
    try {
      const decoded = atob(encodedId);
      if (decoded.startsWith('RC-')) {
        const parts = decoded.split('-');
        return {
          collectionId: parts[1],
          country: parts[2]
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to decode collection ID:', error);
      return null;
    }
  };

  const copyUrlWithFeedback = (url: string, identifier: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(identifier);
    setTimeout(() => {
      setCopiedUrl(null);
    }, 1000);
  };

  const copyAllSupplierLinks = () => {
    if (displayCollections.length === 0) return;

    const allLinks = displayCollections.map((collection: any) => {
      const supplierName = suppliers.find(s => s.id === collection.supplierId)?.name || 'No Supplier';
      const countryName = getCountryName(collection.country);
      const supplierLink = getSupplierLink(collection);
      return `${supplierName} - ${countryName}:\n${supplierLink}`;
    }).join('\n\n');

    navigator.clipboard.writeText(allLinks);
    setCopiedAll(true);
    setTimeout(() => {
      setCopiedAll(false);
    }, 1000);
  };

  // Reset pagination when project changes
  useEffect(() => {
    if (id) {
      setRespondentsOffset(0);
      setAllRespondents([]);
      setHasMoreRespondents(false);
    }
  }, [id]);

  const handleSaveProject = async (updateData: any) => {
    try {
      const result = await projectsApi.update(project!.id, updateData);
      if (result.success) {
        setIsEditing(false);
        queryClient.invalidateQueries(['project', id]);
        queryClient.invalidateQueries('projects');
        setAnalyticsRefreshKey(k => k + 1);
      } else {
        setEditError(result.error || 'Failed to update project');
      }
    } catch (error: any) {
      setEditError(error.response?.data?.error || 'Failed to update project');
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'open'
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]';
  };

  // CR-04: format LOI seconds as HH:MM:SS (matches the client's screenshot)
  const formatLoiHHMMSS = (sec: number): string => {
    if (!sec || sec <= 0) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };


  const copyAllUrls = () => {
    if (!project) return;

    const allUrls = urls.map(({ title, path }) => {
      const url = `${currentDomain}/${path}?projectid=${project.projectId}&mid=XXXX`;
      return `${title}\n${url}`;
    }).join('\n\n');
    copyToClipboard(allUrls);

    setCopiedButton('all');

    setTimeout(() => {
      setCopiedButton(null);
    }, 2000);
  };

  const handleResearchDefenderClick = (respondent: any) => {
    let parsedData = null;
    if (respondent.researchDefenderData) {
      try {
        parsedData = JSON.parse(respondent.researchDefenderData);
      } catch (error) {
        console.error('Error parsing ResearchDefender data:', error);
        parsedData = { error: 'Invalid data format' };
      }
    }

    // Parse stored block reason JSON to extract type and reason
    let blockReason: string | null = null;
    let blockType: string | null = null;
    if (respondent.rdBlockReason) {
      try {
        const parsedReason = JSON.parse(respondent.rdBlockReason);
        blockType = parsedReason.type || null;
        blockReason = parsedReason.reason || null;
      } catch (error) {
        // If not valid JSON, use as plain text
        blockReason = respondent.rdBlockReason;
      }
    }

    let parsedFingerprint = null;
    if (respondent.browserFingerprint) {
      try {
        parsedFingerprint = JSON.parse(respondent.browserFingerprint);
      } catch (error) {
        console.error('Error parsing browser fingerprint:', error);
      }
    }

    setSelectedResearchDefenderData(parsedData);
    setSelectedResearchDefenderId(respondent.maskedId);
    setSelectedRdBlockReason(blockReason);
    setSelectedRdBlockType(blockType);
    setSelectedBrowserFingerprint(parsedFingerprint);
    setShowResearchDefenderModal(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-5 w-40 md-skeleton"></div>
        </div>
        <div className="h-10 w-64 md-skeleton mb-2"></div>
        <div className="h-5 w-48 md-skeleton mb-8"></div>

        <div className="md-card p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i}>
                <div className="h-4 md-skeleton w-1/3 mb-2"></div>
                <div className="h-6 md-skeleton w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || queryError || !project) {
    return (
      <div className="text-center py-20 animate-fade-in-up">
        <div className="text-[#EF4444] text-lg mb-4 font-medium">
          {error || 'Project not found'}
        </div>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="md-btn md-btn-primary"
          >
            Refresh
          </button>
          <Link to="/admin/projects" className="md-btn md-btn-outlined">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start animate-fade-in-up">
        <div>
          <Link
            to="/admin/projects"
            className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#18A5F9] text-sm font-semibold mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
          <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">{project.name}</h1>
          <p className="text-[#9CA3AF] text-sm">
            Project ID: <span className="font-mono font-semibold text-[#6B7280]">{project.projectId}</span>
          </p>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <button
              onClick={handleEditProject}
              className="md-btn md-btn-outlined text-[13px]"
            >
              Edit Project
            </button>
          ) : (
            <button
              onClick={handleCancelEdit}
              className="md-btn md-btn-outlined text-[13px]"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleDuplicateProject}
            className="md-btn md-btn-outlined text-[13px]"
            disabled={duplicateProjectMutation.isLoading}
          >
            {duplicateProjectMutation.isLoading ? 'Duplicating...' : 'Duplicate Project'}
          </button>
          <button
            onClick={handleDeleteProject}
            className="md-btn md-btn-danger text-[13px]"
            disabled={deleteProjectMutation.isLoading}
          >
            {deleteProjectMutation.isLoading ? 'Deleting...' : 'Delete Project'}
          </button>
        </div>
      </div>

      {/* Debug Mode Banner */}
      {project.debugRedirects && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 animate-fade-in-up">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-amber-800">Debug Mode Enabled</h3>
            <p className="text-sm text-amber-700">
              Terminal endpoints (/complete, /terminate, /quota, /security) will show debug pages with redirect URLs instead of performing actual redirects.
            </p>
          </div>
          <button
            onClick={() => setIsEditing(true)}
            className="ml-auto text-sm font-medium text-amber-700 hover:text-amber-800 underline"
          >
            Disable
          </button>
        </div>
      )}

      {/* Project Details */}
      <CollapsibleCard
        title="Project Details"
        className="animate-fade-in-up stagger-1"
        forceOpenWhen={isEditing}
        headerRight={isEditing ? (
          <button
            type="submit"
            form="project-edit-form"
            className="md-btn md-btn-primary text-[13px] px-4 py-2"
          >
            Save Changes
          </button>
        ) : undefined}
      >
        {editError && (
          <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl mb-4">{editError}</div>
        )}

        {isEditing ? (
          <EditProjectForm
            project={project}
            onSave={handleSaveProject}
            onCancel={handleCancelEdit}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1.5 uppercase tracking-wider">Status</label>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
                {project.linksKilled && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                    <span className="w-2 h-2 rounded-full bg-red-600" />
                    LINKS KILLED
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1.5 uppercase tracking-wider">Created</label>
              <div className="text-[15px] font-semibold text-[#374151]">
                {formatDate(project.createdAt)}
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1.5 uppercase tracking-wider">Last Updated</label>
              <div className="text-[15px] font-semibold text-[#374151]">
                {formatDate(project.updatedAt)}
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1.5 uppercase tracking-wider">Project Group</label>
              {project.projectGroupId ? (
                <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-[#E8F4FD] text-[#0B7BC7] border border-[#B3DCFA]">
                  {projectGroups.find(g => g.id === project.projectGroupId)?.name || 'Unknown group'}
                </span>
              ) : (
                <span className="text-[15px] font-semibold text-[#9CA3AF]">None</span>
              )}
            </div>

            <div>
              <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1.5 uppercase tracking-wider">Allow ID Reuse</label>
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${
                project.allowIdReuse
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]'
              }`}>
                {project.allowIdReuse ? 'Yes' : 'No'}
              </span>
            </div>

            <div>
              <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1.5 uppercase tracking-wider">Debug Mode</label>
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${
                project.debugRedirects
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]'
              }`}>
                {project.debugRedirects ? 'Enabled' : 'Disabled'}
              </span>
              {project.debugRedirects && (
                <p className="text-xs text-amber-600 mt-1">Terminal endpoints will show debug pages instead of redirecting</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1.5 uppercase tracking-wider">Notes</label>
              <div className="text-sm text-[#374151] bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3 min-h-[80px] whitespace-pre-wrap">
                {project.notes || 'No notes added'}
              </div>
            </div>
          </div>
        )}
      </CollapsibleCard>

      {/* Quality Screener — project-level default questions */}
      <CollapsibleCard
        title="Quality Screener"
        className="animate-fade-in-up stagger-2"
        headerRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleDownloadScreenerExport()}
              title="Download screener responses CSV"
              className="md-btn md-btn-outlined text-[13px] px-3 py-2 inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </button>
            {screenerDirty ? (
              <button
                type="button"
                onClick={handleSaveProjectScreener}
                disabled={screenerSaving}
                className="md-btn md-btn-primary text-[13px] px-4 py-2"
              >
                {screenerSaving ? 'Saving…' : 'Save Questions'}
              </button>
            ) : null}
          </div>
        }
      >
        <p className="text-sm text-[#9CA3AF] mb-4">
          Select and order trap questions from the{' '}
          <Link to="/admin/quality-questions" className="text-[#0B7BC7] hover:underline font-semibold">
            global question bank
          </Link>
          . These apply to all collections unless a collection sets its own override.
        </p>

        {screenerError && (
          <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl mb-4">{screenerError}</div>
        )}

        {projectScreenerLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 md-skeleton rounded-xl" />
            ))}
          </div>
        ) : (
          <QualityQuestionPicker
            bank={[
              ...qualityBank,
              ...assignedScreenerQuestions.filter(
                (q) => !qualityBank.some((b) => b.id === q.id)
              ),
            ]}
            selectedIds={selectedScreenerIds}
            onToggle={toggleScreenerQuestion}
            onReorder={reorderScreenerQuestion}
            emptyHint={
              qualityBank.length === 0 && assignedScreenerQuestions.length === 0
                ? 'No active questions in the bank. Create questions first.'
                : 'No questions selected — respondents will skip the screener after Research Defender.'
            }
          />
        )}

        {!screenerDirty && assignedScreenerQuestions.length > 0 && (
          <p className="text-xs text-[#9CA3AF] mt-3">
            {assignedScreenerQuestions.length} question{assignedScreenerQuestions.length === 1 ? '' : 's'} assigned to this project.
          </p>
        )}
      </CollapsibleCard>

      {/* Project URLs */}
      <CollapsibleCard
        title="Project URLs"
        className="animate-fade-in-up stagger-2"
        headerRight={(
          <button
            onClick={copyAllUrls}
            className="md-btn md-btn-primary text-[13px] px-4 py-2"
          >
            {copiedButton === 'all' ? 'Copied All URLs!' : 'Copy All URLs'}
          </button>
        )}
      >
        <p className="text-sm text-[#9CA3AF] mb-4">
          Use these URLs for different survey completion states:
        </p>
        <div className="space-y-3">
          {urls.map(({ title, path }) => {
            const url = `${currentDomain}/${path}?projectid=${project.projectId}&mid=XXXX`;
            return (
              <div key={path} className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-[#374151] text-sm">{title}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyUrlWithFeedback(url, path);
                    }}
                    className="text-[13px] text-[#18A5F9] hover:text-[#0D8FDE] font-semibold transition-colors"
                  >
                    {copiedButton === path ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <code className="text-xs text-[#6B7280] block whitespace-nowrap font-mono">
                    {url}
                  </code>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-[#E8F4FD] border border-[#B3DCFA] rounded-xl p-4 mt-4">
          <p className="text-sm text-[#0B7BC7]">
            <strong>Note:</strong> Replace <code className="bg-[#D0ECFC] px-1 rounded">{'XXXX'}</code> with the actual member ID when sending respondents to these URLs.
          </p>
        </div>
      </CollapsibleCard>

      {/* Dropout Alerts */}
      <CollapsibleCard
        title="Dropout Detection"
        className="animate-fade-in-up stagger-3"
        headerRight={(
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-[#9CA3AF] font-medium">Monitoring</span>
          </div>
        )}
      >
        {dropoutLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#18A5F9]"></div>
            <span className="ml-3 text-[#6B7280]">Checking for dropouts...</span>
          </div>
        ) : dropoutResponse?.data?.logs?.length > 0 ? (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="font-bold text-orange-900">
                  {dropoutResponse.data.stats.total} Dropouts Detected
                </span>
              </div>
              <p className="text-sm text-orange-800">
                {dropoutResponse.data.stats.today} today, {dropoutResponse.data.stats.thisWeek} this week
              </p>
            </div>

            <div className="text-sm text-[#9CA3AF] mb-3">
              Recent unknown URL hits (auto-refreshes every 30 seconds)
            </div>

            <div className="space-y-2">
              {dropoutResponse.data.logs.map((dropout: any) => (
                <div key={dropout.id} className="bg-red-50 border border-red-100 rounded-xl p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-semibold text-red-800">
                          {dropout.method} {dropout.url}
                        </span>
                        {dropout.detectedProject && (
                          <span className="text-xs bg-[#E8F4FD] text-[#0B7BC7] px-2 py-1 rounded-full font-semibold">
                            Project: {dropout.detectedProject.name}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6B7280] grid grid-cols-2 md:grid-cols-4 gap-2">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDateTime(dropout.timestamp)}</span>
                        <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {dropout.ipAddress}</span>
                        {dropout.projectid && (
                          <span className="flex items-center gap-1">
                            <BarChart3 className="w-3 h-3" />
                            Project: {dropout.projectid}
                          </span>
                        )}
                        {dropout.rc && <span className="flex items-center gap-1"><User className="w-3 h-3" /> Member: {dropout.rc}</span>}
                      </div>
                      {dropout.userAgent && (
                        <div className="text-xs text-[#9CA3AF] mt-1 truncate">
                          <span className="flex items-center gap-1"><Monitor className="w-3 h-3" /> {dropout.userAgent}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {dropoutResponse.data.stats.topUrls.length > 0 && (
              <div className="mt-4 p-3 bg-[#F9FAFB] rounded-xl border border-[#ECEEF1]">
                <div className="text-sm font-bold text-[#374151] mb-2">Top Problem URLs:</div>
                <div className="space-y-1">
                  {dropoutResponse.data.stats.topUrls.slice(0, 5).map((item: any) => (
                    <div key={item.url} className="flex justify-between text-xs">
                      <span className="font-mono text-[#6B7280]">{item.url}</span>
                      <span className="text-[#EF4444] font-semibold">{item.count} hits</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 bg-emerald-50 rounded-xl border border-emerald-200">
            <div className="text-emerald-600 mb-1 flex items-center justify-center gap-2 font-semibold">
              <CheckCircle className="w-5 h-5" />
              No dropouts detected
            </div>
            <div className="text-sm text-emerald-500">
              All traffic is being captured through proper URLs
            </div>
          </div>
        )}
      </CollapsibleCard>

      {/* Advanced Analytics */}
      <CollapsibleCard
        title="Advanced Analytics"
        className="animate-fade-in-up"
      >
        <ProjectAnalytics
          projectId={project.id}
          projectName={project.name}
          refreshKey={analyticsRefreshKey}
        />
      </CollapsibleCard>

      {/* Activity Log — tabbed: market / supplier / collection / respondent; collapsible (CR-12) */}
      <CollapsibleCard
        title="Activity Log"
        className="animate-fade-in-up stagger-4"
        headerRight={(
          activityView === 'respondent' ? (
            <div className="flex items-center gap-3">
              {/* Subtle Masked ID search — opens a modal */}
              <button
                type="button"
                onClick={handleOpenSearchModal}
                title={appliedSearch ? `Filtering by Masked ID “${appliedSearch}”` : 'Search by Masked ID'}
                aria-label="Search by Masked ID"
                className={`relative p-1.5 rounded-lg transition-colors ${
                  appliedSearch
                    ? 'text-[#18A5F9] bg-[#E8F4FD] hover:bg-[#D0ECFC]'
                    : 'text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F5F7]'
                }`}
              >
                <Search className="w-4 h-4" />
                {appliedSearch && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#18A5F9] rounded-full ring-2 ring-white"></span>
                )}
              </button>
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-[#9CA3AF] font-medium">Live</span>
            </div>
          ) : null
        )}
      >

        {/* View tabs */}
        <div className="flex border-b border-[#ECEEF1] mb-4">
          <button
            type="button"
            onClick={() => setActivityView('market')}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              activityView === 'market'
                ? 'border-[#18A5F9] text-[#18A5F9]'
                : 'border-transparent text-[#6B7280] hover:text-[#374151]'
            }`}
          >
            By Market
          </button>
          <button
            type="button"
            onClick={() => setActivityView('supplier')}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              activityView === 'supplier'
                ? 'border-[#18A5F9] text-[#18A5F9]'
                : 'border-transparent text-[#6B7280] hover:text-[#374151]'
            }`}
          >
            By Supplier
          </button>
          <button
            type="button"
            onClick={() => setActivityView('collection')}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              activityView === 'collection'
                ? 'border-[#18A5F9] text-[#18A5F9]'
                : 'border-transparent text-[#6B7280] hover:text-[#374151]'
            }`}
          >
            By Collection
          </button>
          <button
            type="button"
            onClick={() => setActivityView('respondent')}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              activityView === 'respondent'
                ? 'border-[#18A5F9] text-[#18A5F9]'
                : 'border-transparent text-[#6B7280] hover:text-[#374151]'
            }`}
          >
            By Respondent
          </button>
        </div>

        {activityView === 'respondent' && (respondentsLoading && respondentsOffset === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18A5F9]"></div>
            <span className="ml-3 text-[#6B7280]">Loading activity...</span>
          </div>
        ) : allRespondents?.length > 0 ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center gap-4 mb-3">
              <div className="text-sm text-[#9CA3AF] flex items-center flex-wrap gap-x-2 gap-y-1">
                {appliedSearch ? (
                  <>
                    <span>Filtered by Masked ID</span>
                    <span className="inline-flex items-center gap-1 bg-[#E8F4FD] text-[#0B7BC7] border border-[#B3DCFA] rounded-full pl-2.5 pr-1 py-0.5 font-mono text-xs">
                      {appliedSearch}
                      <button
                        type="button"
                        onClick={handleClearRespondentSearch}
                        className="hover:bg-[#D0ECFC] rounded-full p-0.5"
                        aria-label="Clear filter"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                    <span className="font-semibold text-[#6B7280]">{allRespondents.length} {allRespondents.length === 1 ? 'match' : 'matches'}</span>
                  </>
                ) : (
                  <>
                    <span>Most recent respondent activity {respondentsOffset === 0 && "(auto-refreshes every 30 seconds)"}</span>
                    {allRespondents.length > 0 && (
                      <span className="font-semibold text-[#6B7280]">Showing {allRespondents.length} activities</span>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[13px] text-[#9CA3AF] mr-1">Export:</span>
                <ExportMenu
                  label="CSV"
                  icon={<BarChart3 className="w-4 h-4" />}
                  className="text-[13px] bg-[#E8F4FD] hover:bg-[#D0ECFC] text-[#0B7BC7] px-3 py-1.5 rounded-lg border border-[#B3DCFA] flex items-center gap-1 font-semibold transition-colors"
                  onExport={(statusFilter) => handleDownloadRespondents('csv', { statusFilter })}
                />
                <ExportMenu
                  label="TXT"
                  icon={<FileText className="w-4 h-4" />}
                  className="text-[13px] bg-[#F9FAFB] hover:bg-[#F3F5F7] text-[#6B7280] px-3 py-1.5 rounded-lg border border-[#ECEEF1] flex items-center gap-1 font-semibold transition-colors"
                  onExport={(statusFilter) => handleDownloadRespondents('txt', { statusFilter })}
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[#ECEEF1]">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F9FAFB] border-b border-[#ECEEF1]">
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Time</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Masked ID</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Member ID</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Supplier</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Collection</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Country</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Location</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">RD</th>
                    {canDeleteRespondents && (
                      <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider sr-only">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {allRespondents.map((respondent: any) => (
                    <tr key={respondent.id} className="border-b border-[#ECEEF1] hover:bg-[#F9FAFB] transition-colors">
                      <td className="py-3 px-4 text-sm text-[#374151]">
                        {formatDateTime(respondent.completedAt || respondent.updatedAt)}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono font-semibold text-[#374151]">
                        {respondent.maskedId}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono text-[#6B7280]">
                        {respondent.panelId || '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">
                        {getSupplierName(respondent.collectionId)}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">
                        {getCollectionName(respondent.collectionId)}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">
                        {getCountry(respondent.collectionId)}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6B7280] font-mono">
                        {getRDLocation(respondent)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          respondent.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : respondent.status === 'terminated'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : respondent.status === 'quota_full'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : respondent.status === 'security_terminated'
                            ? 'bg-orange-50 text-orange-700 border border-orange-200'
                            : respondent.status === 'quality_terminated'
                            ? 'bg-violet-50 text-violet-700 border border-violet-200'
                            : respondent.status === 'started'
                            ? 'bg-sky-50 text-sky-700 border border-sky-200'
                            : 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]'
                        }`}>
                          {respondent.status === 'completed' && (
                            <span className="flex items-center gap-1">
                              <Check className="w-3.5 h-3.5" />
                              Completed
                            </span>
                          )}
                          {respondent.status === 'terminated' && (
                            <span className="flex items-center gap-1">
                              <X className="w-3.5 h-3.5" />
                              Terminated
                            </span>
                          )}
                          {respondent.status === 'quota_full' && (
                            <span className="flex items-center gap-1">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Quota Full
                            </span>
                          )}
                          {respondent.status === 'security_terminated' && (
                            <span className="flex items-center gap-1">
                              <Shield className="w-3.5 h-3.5" />
                              Security
                            </span>
                          )}
                          {respondent.status === 'quality_terminated' && (
                            <span className="flex items-center gap-1">
                              <ClipboardList className="w-3.5 h-3.5" />
                              Quality Terminate
                            </span>
                          )}
                          {respondent.status === 'started' && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              Started
                            </span>
                          )}
                          {respondent.status === 'invited' && 'Invited'}
                          {!['completed', 'terminated', 'quota_full', 'security_terminated', 'quality_terminated', 'started', 'invited'].includes(respondent.status) && (respondent.status || '—')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {respondent.researchDefenderData ? (
                          <button
                            onClick={() => handleResearchDefenderClick(respondent)}
                            className="text-[#18A5F9] hover:text-[#0D8FDE] hover:bg-[#E8F4FD] px-2.5 py-1 rounded-lg text-[13px] font-semibold transition-all"
                          >
                            View
                          </button>
                        ) : (
                          <span className="text-[#9CA3AF] text-[13px]">—</span>
                        )}
                      </td>
                      {canDeleteRespondents && (
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleDeleteRespondent(respondent)}
                            disabled={deleteRespondentMutation.isLoading}
                            title="Delete test data"
                            aria-label="Delete respondent"
                            className="text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMoreRespondents && (
              <div className="text-center mt-4">
                <button
                  onClick={handleLoadMoreRespondents}
                  disabled={isLoadingMore}
                  className="md-btn md-btn-outlined text-[13px]"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Loading more...
                    </>
                  ) : (
                    'Load More Activities'
                  )}
                </button>
              </div>
            )}

            {!hasMoreRespondents && allRespondents.length > 0 && (
              <div className="text-[13px] text-[#9CA3AF] text-center mt-4 font-medium">
                Showing all {allRespondents.length} activities
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            {appliedSearch ? (
              <>
                <div className="text-[#6B7280] mb-2 flex items-center justify-center gap-1 font-medium"><Search className="w-5 h-5" /> No respondents match that Masked ID</div>
                <div className="text-sm text-[#9CA3AF]">
                  No activity found for “<span className="font-mono">{appliedSearch}</span>” in this project.
                  <br />
                  <button type="button" onClick={handleClearRespondentSearch} className="text-[#18A5F9] hover:text-[#0D8FDE] font-semibold mt-1">
                    Clear search
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-[#6B7280] mb-2 flex items-center justify-center gap-1 font-medium"><ClipboardList className="w-5 h-5" /> No activity recorded yet</div>
                <div className="text-sm text-[#9CA3AF]">
                  Activity will appear here when respondents visit your tracking URLs
                  <br />
                  Export options will be available once activity is recorded
                </div>
              </>
            )}
          </div>
        ))}

        {activityView === 'market' && (marketBreakdown.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">No market activity yet for this project.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center gap-4 mb-3">
              <div className="text-sm text-[#9CA3AF]">
                Activity totals grouped by market (country)
                <span className="ml-2 font-semibold text-[#6B7280]">
                  Showing {marketBreakdown.length} {marketBreakdown.length === 1 ? 'market' : 'markets'}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[13px] text-[#9CA3AF] mr-1">Export:</span>
                <button
                  type="button"
                  onClick={handleDownloadMarketSummary}
                  title="Download market summary CSV"
                  className="text-[13px] bg-[#E8F4FD] hover:bg-[#D0ECFC] text-[#0B7BC7] px-3 py-1.5 rounded-lg border border-[#B3DCFA] flex items-center gap-1 font-semibold transition-colors"
                >
                  <BarChart3 className="w-4 h-4" />
                  CSV
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-xl border border-[#ECEEF1]">
              <table className="w-full">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#ECEEF1]">
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Market</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Completes</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Terminates</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Quota Full</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Security</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Quality</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">IR</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Mean LOI</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Median LOI</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Export</th>
                </tr>
              </thead>
              <tbody>
                {marketBreakdown.map((row) => {
                  const label = row.country ? getCountryName(row.country) : 'Unknown';
                  return (
                  <tr key={row.country || 'unknown'} className="border-b border-[#ECEEF1] hover:bg-[#F9FAFB] transition-colors">
                    <td className="py-3 px-4 text-sm font-semibold text-[#1A1F2B]">{label}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.completes}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.terminates}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.quotaFull}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.security}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.quality ?? 0}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{Number(row.ir).toFixed(2)}%</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151] font-mono">{formatLoiHHMMSS(row.meanLoiSeconds)}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151] font-mono">{formatLoiHHMMSS(row.medianLoiSeconds)}</td>
                    <td className="text-right py-3 px-4">
                      <div className="inline-flex justify-end">
                        <ExportMenu
                          label="CSV"
                          icon={<BarChart3 className="w-3.5 h-3.5" />}
                          className="text-[13px] text-[#0B7BC7] hover:text-[#0D8FDE] hover:underline inline-flex items-center gap-1 font-semibold transition-colors"
                          onExport={(statusFilter) => handleDownloadRespondents('csv', { statusFilter, country: row.country, marketName: label })}
                        />
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              {marketTotals && (
                <tfoot>
                  <tr className="border-t-2 border-[#ECEEF1] bg-[#F9FAFB] font-bold text-[#1A1F2B]">
                    <td className="py-3 px-4 text-sm">Total</td>
                    <td className="text-right py-3 px-4 text-sm">{marketTotals.completes}</td>
                    <td className="text-right py-3 px-4 text-sm">{marketTotals.terminates}</td>
                    <td className="text-right py-3 px-4 text-sm">{marketTotals.quotaFull}</td>
                    <td className="text-right py-3 px-4 text-sm">{marketTotals.security}</td>
                    <td className="text-right py-3 px-4 text-sm">{marketTotals.quality ?? 0}</td>
                    <td className="text-right py-3 px-4 text-sm">{Number(marketTotals.ir).toFixed(2)}%</td>
                    <td className="text-right py-3 px-4 text-sm font-mono">{formatLoiHHMMSS(marketTotals.meanLoiSeconds)}</td>
                    <td className="text-right py-3 px-4 text-sm font-mono">{formatLoiHHMMSS(marketTotals.medianLoiSeconds)}</td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          </div>
        ))}

        {activityView === 'supplier' && (supplierBreakdown.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">No supplier activity yet for this project.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center gap-4 mb-3">
              <div className="text-sm text-[#9CA3AF]">
                Activity totals grouped by supplier
                <span className="ml-2 font-semibold text-[#6B7280]">
                  Showing {supplierBreakdown.length} {supplierBreakdown.length === 1 ? 'supplier' : 'suppliers'}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[13px] text-[#9CA3AF] mr-1">Export:</span>
                {/* CR-05: all-suppliers CSV = the aggregated per-supplier summary table */}
                <button
                  type="button"
                  onClick={handleDownloadSupplierSummary}
                  title="Download supplier summary CSV"
                  className="text-[13px] bg-[#E8F4FD] hover:bg-[#D0ECFC] text-[#0B7BC7] px-3 py-1.5 rounded-lg border border-[#B3DCFA] flex items-center gap-1 font-semibold transition-colors"
                >
                  <BarChart3 className="w-4 h-4" />
                  CSV
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-xl border border-[#ECEEF1]">
              <table className="w-full">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#ECEEF1]">
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Supplier</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Completes</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Terminates</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Quota Full</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Security</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Quality</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">IR</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Mean LOI</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Median LOI</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Export</th>
                </tr>
              </thead>
              <tbody>
                {supplierBreakdown.map((row) => (
                  <tr key={row.supplierId} className="border-b border-[#ECEEF1] hover:bg-[#F9FAFB] transition-colors">
                    <td className="py-3 px-4 text-sm font-semibold text-[#1A1F2B]">{row.name}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.completes}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.terminates}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.quotaFull}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.security}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.quality ?? 0}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{Number(row.ir).toFixed(2)}%</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151] font-mono">{formatLoiHHMMSS(row.meanLoiSeconds)}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151] font-mono">{formatLoiHHMMSS(row.medianLoiSeconds)}</td>
                    <td className="text-right py-3 px-4">
                      {/* CR-05: per-supplier CSV download */}
                      <div className="inline-flex justify-end">
                        <ExportMenu
                          label="CSV"
                          icon={<BarChart3 className="w-3.5 h-3.5" />}
                          className="text-[13px] text-[#0B7BC7] hover:text-[#0D8FDE] hover:underline inline-flex items-center gap-1 font-semibold transition-colors"
                          onExport={(statusFilter) => handleDownloadRespondents('csv', { statusFilter, supplierId: row.supplierId, supplierName: row.name })}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {supplierTotals && (
                <tfoot>
                  <tr className="border-t-2 border-[#ECEEF1] bg-[#F9FAFB] font-bold text-[#1A1F2B]">
                    <td className="py-3 px-4 text-sm">Total</td>
                    <td className="text-right py-3 px-4 text-sm">{supplierTotals.completes}</td>
                    <td className="text-right py-3 px-4 text-sm">{supplierTotals.terminates}</td>
                    <td className="text-right py-3 px-4 text-sm">{supplierTotals.quotaFull}</td>
                    <td className="text-right py-3 px-4 text-sm">{supplierTotals.security}</td>
                    <td className="text-right py-3 px-4 text-sm">{supplierTotals.quality ?? 0}</td>
                    <td className="text-right py-3 px-4 text-sm">{Number(supplierTotals.ir).toFixed(2)}%</td>
                    <td className="text-right py-3 px-4 text-sm font-mono">{formatLoiHHMMSS(supplierTotals.meanLoiSeconds)}</td>
                    <td className="text-right py-3 px-4 text-sm font-mono">{formatLoiHHMMSS(supplierTotals.medianLoiSeconds)}</td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          </div>
        ))}

        {activityView === 'collection' && (collectionBreakdown.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">No collection activity yet for this project.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center gap-4 mb-3">
              <div className="text-sm text-[#9CA3AF]">
                Activity totals grouped by collection
                <span className="ml-2 font-semibold text-[#6B7280]">
                  Showing {collectionBreakdown.length} {collectionBreakdown.length === 1 ? 'collection' : 'collections'}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[13px] text-[#9CA3AF] mr-1">Export:</span>
                {/* CR-08: all-collections CSV = the aggregated per-collection summary table */}
                <button
                  type="button"
                  onClick={handleDownloadCollectionSummary}
                  title="Download collection summary CSV"
                  className="text-[13px] bg-[#E8F4FD] hover:bg-[#D0ECFC] text-[#0B7BC7] px-3 py-1.5 rounded-lg border border-[#B3DCFA] flex items-center gap-1 font-semibold transition-colors"
                >
                  <BarChart3 className="w-4 h-4" />
                  CSV
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-xl border border-[#ECEEF1]">
              <table className="w-full">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#ECEEF1]">
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Collection</th>
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Country</th>
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Supplier</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Completes</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Terminates</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Quota Full</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Security</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Quality</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">IR</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Mean LOI</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Median LOI</th>
                  <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Export</th>
                </tr>
              </thead>
              <tbody>
                {collectionBreakdown.map((row) => (
                  <tr key={row.collectionId} className="border-b border-[#ECEEF1] hover:bg-[#F9FAFB] transition-colors">
                    <td className="py-3 px-4 text-sm font-semibold text-[#1A1F2B]">{row.name}</td>
                    <td className="py-3 px-4 text-sm text-[#374151]">{row.country || '-'}</td>
                    <td className="py-3 px-4 text-sm text-[#374151]">{row.supplierName || '-'}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.completes}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.terminates}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.quotaFull}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.security}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{row.quality ?? 0}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151]">{Number(row.ir).toFixed(2)}%</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151] font-mono">{formatLoiHHMMSS(row.meanLoiSeconds)}</td>
                    <td className="text-right py-3 px-4 text-sm text-[#374151] font-mono">{formatLoiHHMMSS(row.medianLoiSeconds)}</td>
                    <td className="text-right py-3 px-4">
                      {/* CR-08: per-collection CSV download + screener export */}
                      <div className="inline-flex justify-end items-center gap-3">
                        <button
                          type="button"
                          onClick={() => handleDownloadScreenerExport({ collectionId: row.collectionId, collectionName: row.name })}
                          title="Download screener responses CSV"
                          className="text-[13px] text-[#0B7BC7] hover:text-[#0D8FDE] hover:underline inline-flex items-center gap-1 font-semibold transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Screener
                        </button>
                        <ExportMenu
                          label="CSV"
                          icon={<BarChart3 className="w-3.5 h-3.5" />}
                          className="text-[13px] text-[#0B7BC7] hover:text-[#0D8FDE] hover:underline inline-flex items-center gap-1 font-semibold transition-colors"
                          onExport={(statusFilter) => handleDownloadRespondents('csv', { statusFilter, collectionId: row.collectionId, collectionName: row.name })}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {collectionTotals && (
                <tfoot>
                  <tr className="border-t-2 border-[#ECEEF1] bg-[#F9FAFB] font-bold text-[#1A1F2B]">
                    <td className="py-3 px-4 text-sm">Total</td>
                    <td className="py-3 px-4 text-sm"></td>
                    <td className="py-3 px-4 text-sm"></td>
                    <td className="text-right py-3 px-4 text-sm">{collectionTotals.completes}</td>
                    <td className="text-right py-3 px-4 text-sm">{collectionTotals.terminates}</td>
                    <td className="text-right py-3 px-4 text-sm">{collectionTotals.quotaFull}</td>
                    <td className="text-right py-3 px-4 text-sm">{collectionTotals.security}</td>
                    <td className="text-right py-3 px-4 text-sm">{collectionTotals.quality ?? 0}</td>
                    <td className="text-right py-3 px-4 text-sm">{Number(collectionTotals.ir).toFixed(2)}%</td>
                    <td className="text-right py-3 px-4 text-sm font-mono">{formatLoiHHMMSS(collectionTotals.meanLoiSeconds)}</td>
                    <td className="text-right py-3 px-4 text-sm font-mono">{formatLoiHHMMSS(collectionTotals.medianLoiSeconds)}</td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          </div>
        ))}
      </CollapsibleCard>

      {/* Collections */}
      <CollapsibleCard
        title="Collections"
        className="animate-fade-in-up stagger-5"
        headerRight={(
          <div className="flex items-center gap-3">
            {collections.length > 0 && (
              <button
                onClick={copyAllSupplierLinks}
                className="text-[13px] text-[#6B7280] hover:text-[#18A5F9] font-semibold transition-colors"
              >
                {copiedAll ? 'Copied!' : 'Copy All Links'}
              </button>
            )}
            <button
              onClick={() => setShowCreateCollectionModal(true)}
              className="md-btn md-btn-primary text-[13px] px-4 py-2"
            >
              Add Collection
            </button>
          </div>
        )}
      >
        <p className="text-sm text-[#9CA3AF] mb-4">
          Country-specific survey URLs and configurations for this project:
        </p>

        {collectionsLoading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-16 md-skeleton rounded-xl"></div>
            ))}
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-8 bg-[#F9FAFB] rounded-xl border border-[#ECEEF1]">
            <p className="text-[#6B7280] mb-4">No collections yet. Add your first collection to get started.</p>
            <button
              onClick={() => setShowCreateCollectionModal(true)}
              className="md-btn md-btn-primary text-[13px]"
            >
              Create First Collection
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-4 mb-4 pb-4 border-b border-[#ECEEF1]">
              <div>
                <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Sort by</label>
                <div className="md-outlined-textfield !my-0">
                  <select
                    value={sortField}
                    onChange={(e) => applyCollectionSort(e.target.value as CollectionSortField, sortDirection)}
                    className="w-full min-w-[160px] !h-12 !py-0"
                  >
                    {COLLECTION_SORT_FIELDS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Order</label>
                <div className="md-outlined-textfield !my-0">
                  <select
                    value={sortDirection}
                    onChange={(e) => applyCollectionSort(sortField, e.target.value as CollectionSortDirection)}
                    className="w-full min-w-[140px] !h-12 !py-0"
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>
              {isCollectionOrderDirty && (
                <div>
                  <label
                    className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider invisible select-none"
                    aria-hidden="true"
                  >
                    Save
                  </label>
                  <button
                    type="button"
                    onClick={handleSaveCollectionOrder}
                    disabled={saveCollectionOrderMutation.isLoading}
                    className="md-btn md-btn-primary !h-12 !min-h-0 !py-0 px-4 text-[15px] leading-none"
                  >
                    {saveCollectionOrderMutation.isLoading ? 'Saving...' : 'Save Order'}
                  </button>
                </div>
              )}
            </div>
            <p className="text-xs text-[#9CA3AF] -mt-1 mb-2">
              Drag collections to reorder, or use the sort controls above. Save when you are happy with the order.
            </p>
            {displayCollections.map((collection: any) => {
              const sup = collection.supplierId ? suppliers.find(s => s.id === collection.supplierId) : undefined;
              const rd = collectionRdSettings(collection, sup);
              return (
              <div
                key={collection.id}
                draggable
                onDragStart={() => handleCollectionDragStart(collection.id)}
                onDragOver={(e) => handleCollectionDragOver(e, collection.id)}
                onDrop={(e) => handleCollectionDrop(e, collection.id)}
                onDragEnd={handleCollectionDragEnd}
                className={`bg-[#F9FAFB] border rounded-xl p-4 transition-colors ${
                  dragOverCollectionId === collection.id
                    ? 'border-[#18A5F9] shadow-[0_0_0_1px_#18A5F9]'
                    : 'border-[#ECEEF1] hover:border-[#18A5F9]/20'
                } ${draggedCollectionId === collection.id ? 'opacity-50' : ''}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex flex-1 gap-3 min-w-0">
                    <button
                      type="button"
                      className="cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#6B7280] shrink-0 mt-0.5"
                      title="Drag to reorder"
                      aria-label="Drag to reorder"
                    >
                      <GripVertical className="w-5 h-5" />
                    </button>
                    <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-[#1A1F2B] mb-1">
                      {collection.name}
                    </h4>
                    <div className="flex items-center gap-4 text-sm text-[#6B7280] mb-2">
                      <span className="flex items-center gap-1"><Globe className="w-4 h-4" /> {getCountryName(collection.country)}</span>
                      {collection.supplierId && suppliers.find(s => s.id === collection.supplierId) && (
                        <span className="flex items-center gap-1"><Building className="w-4 h-4" /> {suppliers.find(s => s.id === collection.supplierId)?.name}</span>
                      )}
                      {collection.totalTarget && (
                        <span className="flex items-center gap-1"><Target className="w-4 h-4" /> Target: {collection.totalTarget.toLocaleString()}</span>
                      )}
                    </div>
                    {/* ResearchDefender settings — always visible per collection */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mr-1">RD</span>
                      {([
                        ['Enabled', rd.rdEnabled],
                        ['Duplicates', rd.duplicates],
                        ['Geo', rd.geo],
                      ] as [string, boolean][]).map(([label, on]) => (
                        <span
                          key={label}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          {label}
                        </span>
                      ))}
                    </div>
                    <div className="text-sm">
                      <span className="text-[#9CA3AF]">URL: </span>
                      <span
                        onClick={() => copyUrlWithFeedback(collection.url, `url-${collection.id}`)}
                        className="text-[#6B7280] hover:bg-[#ECEEF1] break-all cursor-pointer px-1 -mx-1 rounded-lg font-mono text-xs transition-colors"
                        title="Click to copy"
                      >
                        {collection.url}
                      </span>
                      {copiedUrl === `url-${collection.id}` && (
                        <span className="text-xs text-emerald-600 ml-2 font-semibold">Copied!</span>
                      )}
                    </div>
                    <div className="text-sm mt-2">
                      <span className="text-[#9CA3AF]">Supplier Link: </span>
                      <span
                        onClick={() => copyUrlWithFeedback(getSupplierLink(collection), `supplier-${collection.id}`)}
                        className="text-[#6B7280] hover:bg-[#ECEEF1] break-all cursor-pointer px-1 -mx-1 rounded-lg font-mono text-xs transition-colors"
                        title="Click to copy"
                      >
                        {getSupplierLink(collection)}
                      </span>
                      {copiedUrl === `supplier-${collection.id}` && (
                        <span className="text-xs text-emerald-600 ml-2 font-semibold">Copied!</span>
                      )}
                    </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleDownloadScreenerExport({ collectionId: collection.id, collectionName: collection.name })}
                      className="md-btn md-btn-outlined text-[13px] px-3 py-1 inline-flex items-center gap-1"
                      title="Download screener responses CSV"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Screener
                    </button>
                    <button
                      onClick={() => setEditingCollection(collection)}
                      className="md-btn md-btn-outlined text-[13px] px-3 py-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCollection(collection.id, collection.name)}
                      className="md-btn md-btn-danger text-[13px] px-3 py-1"
                      disabled={deleteCollectionMutation.isLoading}
                    >
                      {deleteCollectionMutation.isLoading ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CollapsibleCard>

      {/* Reconciliation Section */}
      <CollapsibleCard
        title="Reconciliation"
        className="animate-fade-in-up"
      >
        <ReconciliationSection
          projectId={project.id}
          projectName={project.name}
        />
      </CollapsibleCard>

      {/* Create/Edit Collection Modal */}
      {(showCreateCollectionModal || editingCollection) && (
        <CollectionModal
          project={project}
          collection={editingCollection}
          suppliersList={suppliers}
          qualityBank={qualityBank}
          queryClient={queryClient}
          copiedUrl={copiedUrl}
          copyUrlWithFeedback={copyUrlWithFeedback}
          onClose={() => {
            setShowCreateCollectionModal(false);
            setEditingCollection(null);
          }}
          onSuccess={() => {
            setShowCreateCollectionModal(false);
            setEditingCollection(null);
            queryClient.invalidateQueries(['project-collections', id]);
          }}
        />
      )}

        {/* ResearchDefender Modal */}
        <ResearchDefenderModal
          isOpen={showResearchDefenderModal}
          onClose={() => {
            setShowResearchDefenderModal(false);
            setSelectedResearchDefenderData(null);
            setSelectedResearchDefenderId('');
            setSelectedRdBlockReason(null);
            setSelectedRdBlockType(null);
            setSelectedBrowserFingerprint(null);
          }}
          data={selectedResearchDefenderData}
          maskedId={selectedResearchDefenderId}
          blockReason={selectedRdBlockReason}
          blockType={selectedRdBlockType}
          browserFingerprint={selectedBrowserFingerprint}
        />

        {/* Masked ID search modal — opened from the Activity Log search icon */}
        {showSearchModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSearchModal(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#ECEEF1]">
                <h3 className="text-lg font-bold font-heading text-[#1A1F2B] flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#18A5F9]" />
                  Search by Masked ID
                </h3>
                <button
                  type="button"
                  onClick={() => setShowSearchModal(false)}
                  className="text-[#9CA3AF] hover:text-[#374151] p-1 rounded-lg hover:bg-[#F3F5F7]"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-[#6B7280]">
                  Enter a Masked ID to show just that respondent's row in the activity log.
                </p>
                <div className="relative">
                  <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    autoFocus
                    value={maskedIdSearch}
                    onChange={(e) => setMaskedIdSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchRespondents(); }}
                    placeholder="e.g. RCMQ934BRGAB1FF1B4C021C425"
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-[#ECEEF1] rounded-lg bg-white focus:ring-2 focus:ring-[#18A5F9] focus:border-[#18A5F9] font-mono"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 px-6 py-4 border-t border-[#ECEEF1]">
                {appliedSearch && (
                  <button
                    type="button"
                    onClick={handleClearRespondentSearch}
                    className="md-btn md-btn-outlined text-[13px]"
                  >
                    Clear filter
                  </button>
                )}
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => setShowSearchModal(false)}
                  className="md-btn md-btn-outlined text-[13px]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSearchRespondents}
                  disabled={!maskedIdSearch.trim()}
                  className={`md-btn text-[13px] ${maskedIdSearch.trim() ? 'md-btn-primary' : 'bg-[#ECEEF1] text-[#9CA3AF] cursor-not-allowed'}`}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};


// Create/Edit Collection Modal Component
const CollectionModal = ({
  project,
  collection,
  suppliersList,
  qualityBank,
  queryClient,
  copiedUrl,
  copyUrlWithFeedback,
  onClose,
  onSuccess
}: {
  project: Project;
  collection?: any;
  suppliersList: any[];
  qualityBank: QualityQuestion[];
  queryClient: any;
  copiedUrl: string | null;
  copyUrlWithFeedback: (url: string, identifier: string) => void;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  // Get current supplier for RD / URL defaults
  const currentSupplier = collection?.supplierId 
    ? suppliersList.find((s: any) => s.id === collection.supplierId)
    : null;

  const [formData, setFormData] = useState({
    name: collection?.name || '',
    country: collection?.country || '',
    url: collection?.url || '',
    totalTarget: collection?.totalTarget || '',
    supplierId: collection?.supplierId || '',
    // Research Defender settings - use collection overrides if set, otherwise use supplier defaults
    rdEnabled: collection?.rdEnabled ?? currentSupplier?.rdEnabled ?? true,
    rdTriggerScore: collection?.rdTriggerScore ?? currentSupplier?.rdTriggerScore ?? 0,
    rdBlockDuplicates: collection?.rdBlockDuplicates ?? currentSupplier?.rdBlockDuplicates ?? true,
    enableGeoBlocker: collection?.enableGeoBlocker ?? currentSupplier?.enableGeoBlocker ?? false,
    // URL templates - collection override if set, otherwise supplier defaults
    completeUrlTemplate: collection?.completeUrlTemplate || currentSupplier?.completeUrlTemplate || '',
    terminateUrlTemplate: collection?.terminateUrlTemplate || currentSupplier?.terminateUrlTemplate || '',
    quotaFullUrlTemplate: collection?.quotaFullUrlTemplate || currentSupplier?.quotaFullUrlTemplate || '',
    securityTerminateUrlTemplate: collection?.securityTerminateUrlTemplate || currentSupplier?.securityTerminateUrlTemplate || '',
  });

  const [useCustomScreener, setUseCustomScreener] = useState(false);
  const [collectionScreenerIds, setCollectionScreenerIds] = useState<string[]>([]);
  const [screenerLoaded, setScreenerLoaded] = useState(!collection);

  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '' });
  const [error, setError] = useState<string>('');
  const [supplierError, setSupplierError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [createdCollection, setCreatedCollection] = useState<any>(null);
  const [currentDomain] = useState(window.location.origin);
  const [copiedButton, setCopiedButton] = useState<string | null>(null);

  useEffect(() => {
    if (!collection?.id) {
      setScreenerLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await qualityQuestionsApi.getCollectionAssignments(collection.id);
        if (cancelled) return;
        const ids = (res.data || []).map((q: QualityQuestion) => q.id);
        setCollectionScreenerIds(ids);
        setUseCustomScreener(ids.length > 0 || Boolean(res.meta?.hasOverride));
      } catch (err) {
        console.error('Failed to load collection screener questions:', err);
      } finally {
        if (!cancelled) setScreenerLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collection?.id]);

  const saveCollectionScreener = async (collectionId: string) => {
    const questionIds = useCustomScreener ? collectionScreenerIds : [];
    await qualityQuestionsApi.setCollectionAssignments(collectionId, questionIds);
    queryClient.invalidateQueries(['collection-quality-questions', collectionId]);
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      setSupplierError('Supplier name is required');
      return;
    }

    try {
      const result = await suppliersApi.create({ name: newSupplier.name });
      if (result.success) {
        queryClient.invalidateQueries('suppliers');
        setNewSupplier({ name: '' });
        setShowAddSupplier(false);
        setSupplierError('');
        if (result.data?.id) {
          const created = result.data;
          setFormData(prev => ({
            ...prev,
            supplierId: created.id,
            rdEnabled: created.rdEnabled ?? true,
            rdTriggerScore: created.rdTriggerScore ?? 0,
            rdBlockDuplicates: created.rdBlockDuplicates ?? true,
            enableGeoBlocker: created.enableGeoBlocker ?? false,
            completeUrlTemplate: created.completeUrlTemplate || '',
            terminateUrlTemplate: created.terminateUrlTemplate || '',
            quotaFullUrlTemplate: created.quotaFullUrlTemplate || '',
            securityTerminateUrlTemplate: created.securityTerminateUrlTemplate || '',
          }));
        }
      } else {
        setSupplierError(result.error || 'Failed to create supplier');
      }
    } catch (error: any) {
      setSupplierError(error.response?.data?.error || 'Failed to create supplier');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.country.trim() || !formData.url.trim()) {
      setError('All fields are required');
      return;
    }

    try {
      new URL(formData.url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    if (formData.totalTarget && (isNaN(Number(formData.totalTarget)) || Number(formData.totalTarget) < 0)) {
      setError('Total Target must be a positive number');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const collectionData = {
        name: formData.name.trim(),
        country: formData.country,
        url: formData.url.trim(),
        totalTarget: formData.totalTarget ? Number(formData.totalTarget) : null,
        projectId: project.id,
        supplierId: formData.supplierId && formData.supplierId.trim() !== '' ? formData.supplierId : null,
        // Research Defender settings - ensure rdTriggerScore is a number
        rdEnabled: formData.rdEnabled,
        rdTriggerScore: Number(formData.rdTriggerScore),
        rdBlockDuplicates: formData.rdBlockDuplicates,
        enableGeoBlocker: formData.enableGeoBlocker,
        // URL overrides - send null if empty to use supplier defaults
        completeUrlTemplate: formData.completeUrlTemplate?.trim() || null,
        terminateUrlTemplate: formData.terminateUrlTemplate?.trim() || null,
        quotaFullUrlTemplate: formData.quotaFullUrlTemplate?.trim() || null,
        securityTerminateUrlTemplate: formData.securityTerminateUrlTemplate?.trim() || null,
      };

      let result;
      if (collection) {
        const updateData = {
          name: collectionData.name,
          country: collectionData.country,
          url: collectionData.url,
          totalTarget: collectionData.totalTarget,
          supplierId: formData.supplierId && formData.supplierId.trim() !== '' ? formData.supplierId : null,
          // Research Defender settings - ensure rdTriggerScore is a number
          rdEnabled: formData.rdEnabled,
          rdTriggerScore: Number(formData.rdTriggerScore),
          rdBlockDuplicates: formData.rdBlockDuplicates,
          enableGeoBlocker: formData.enableGeoBlocker,
          // URL overrides - send null if empty to use supplier defaults
          completeUrlTemplate: formData.completeUrlTemplate?.trim() || null,
          terminateUrlTemplate: formData.terminateUrlTemplate?.trim() || null,
          quotaFullUrlTemplate: formData.quotaFullUrlTemplate?.trim() || null,
          securityTerminateUrlTemplate: formData.securityTerminateUrlTemplate?.trim() || null,
        };
        result = await collectionsApi.update(collection.id, updateData);
      } else {
        result = await collectionsApi.create(collectionData);
      }

      if (result.success) {
        const savedId = collection ? collection.id : result.data?.id;
        // New collections with no custom screener inherit project defaults —
        // skip the assignment write (empty PUT is a no-op but still gated).
        const shouldSaveScreener = Boolean(collection) || useCustomScreener;
        if (savedId && shouldSaveScreener) {
          try {
            await saveCollectionScreener(savedId);
          } catch (screenerErr: any) {
            setError(
              screenerErr.response?.data?.error ||
                'Collection saved but quality screener questions failed to update'
            );
            setIsLoading(false);
            return;
          }
        }
        if (collection) {
          onSuccess();
        } else {
          setCreatedCollection(result.data);
        }
      } else {
        setError(result.error || `Failed to ${collection ? 'update' : 'create'} collection`);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || `Failed to ${collection ? 'update' : 'create'} collection`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    // When supplier changes, pre-fill RD settings and redirect URLs from supplier
    if (name === 'supplierId') {
      const selectedSupplier = value
        ? suppliersList.find((s: any) => s.id === value)
        : null;
      setFormData(prev => ({
        ...prev,
        supplierId: value,
        rdEnabled: selectedSupplier?.rdEnabled ?? true,
        rdTriggerScore: selectedSupplier?.rdTriggerScore ?? 0,
        rdBlockDuplicates: selectedSupplier?.rdBlockDuplicates ?? true,
        enableGeoBlocker: selectedSupplier?.enableGeoBlocker ?? false,
        completeUrlTemplate: selectedSupplier?.completeUrlTemplate || '',
        terminateUrlTemplate: selectedSupplier?.terminateUrlTemplate || '',
        quotaFullUrlTemplate: selectedSupplier?.quotaFullUrlTemplate || '',
        securityTerminateUrlTemplate: selectedSupplier?.securityTerminateUrlTemplate || '',
      }));
      setError('');
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: name === 'rdTriggerScore' ? Number(newValue) : newValue,
    }));
    setError('');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };


  const copyUrl = () => {
    if (!createdCollection) return;

    const selectedSupplier = suppliersList.find(s => s.id === createdCollection.supplierId);
    const placeholderToken = selectedSupplier?.placeholderToken || 'XXXX';

    const uniqueId = `RC|${createdCollection.id}|${createdCollection.country}`;
    const encodedId = btoa(uniqueId).replace(/[^a-zA-Z0-9]/g, '');
    const url = `${currentDomain}/survey?collection=${encodedId}&rc=${placeholderToken}`;

    copyToClipboard(url);

    setCopiedButton('main');

    setTimeout(() => {
      setCopiedButton(null);
    }, 2000);
  };

  const handleClose = () => {
    setCreatedCollection(null);
    setFormData({
      name: '',
      country: '',
      url: '',
      totalTarget: '',
      supplierId: '',
      rdEnabled: true,
      rdTriggerScore: 0,
      rdBlockDuplicates: true,
      enableGeoBlocker: false,
      completeUrlTemplate: '',
      terminateUrlTemplate: '',
      quotaFullUrlTemplate: '',
      securityTerminateUrlTemplate: '',
    });
    onClose();
    onSuccess();
  };

  const getCollectionUrl = () => {
    if (!createdCollection) return '';

    const selectedSupplier = suppliersList.find(s => s.id === createdCollection.supplierId);
    const placeholderToken = selectedSupplier?.placeholderToken || 'XXXX';

    const uniqueId = `RC|${createdCollection.id}|${createdCollection.country}`;
    const encodedId = btoa(uniqueId).replace(/[^a-zA-Z0-9]/g, '');
    return `${currentDomain}/survey?collection=${encodedId}&rc=${placeholderToken}`;
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1] max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">
          {createdCollection ? 'Collection Created Successfully!' : (collection ? 'Edit Collection' : 'Create Collection')}
        </h2>

        {createdCollection ? (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="text-lg font-bold text-[#1A1F2B] mb-2">
                {createdCollection.name}
              </div>
              <div className="text-sm text-[#6B7280]">
                Country: {createdCollection.country} &bull; Supplier: {suppliersList.find(s => s.id === createdCollection.supplierId)?.name || 'None'}
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-[#1A1F2B]">Supplier Link</h3>
              <button
                onClick={copyUrl}
                className="md-btn md-btn-primary text-[13px] px-4 py-2"
              >
                {copiedButton === 'main' ? 'Copied Link!' : 'Copy Link'}
              </button>
            </div>
            <p className="text-sm text-[#9CA3AF] mb-4">
              Use this URL for respondents to access your survey:
            </p>
            <div className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-4">
              <div className="overflow-x-auto">
                <div className="flex items-center">
                  <code
                    onClick={() => copyUrlWithFeedback(getCollectionUrl(), 'modal-url')}
                    className="text-sm text-[#6B7280] block whitespace-nowrap font-mono cursor-pointer hover:bg-[#ECEEF1] rounded-lg px-2 py-1 -mx-2 -my-1 flex-1 transition-colors"
                    title="Click to copy"
                  >
                    {getCollectionUrl()}
                  </code>
                  {copiedUrl === 'modal-url' && (
                    <span className="text-xs text-emerald-600 ml-3 whitespace-nowrap font-semibold">Copied!</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-[#E8F4FD] border border-[#B3DCFA] rounded-xl p-4 mt-4">
              <p className="text-sm text-[#0B7BC7]">
                <strong>Note:</strong> The URL already contains the supplier's placeholder token.
                The token will be replaced with the actual member ID when respondents access this URL.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button onClick={handleClose} className="md-btn md-btn-primary flex-1">
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Collection Name *</label>
            <div className="md-outlined-textfield">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full"
                placeholder="e.g., US Consumer Survey"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Supplier *</label>
            <div className="space-y-2">
              <div className="md-outlined-textfield">
                <select
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={handleChange}
                  className="w-full"
                  disabled={!!collection}
                  title={collection ? "Supplier cannot be changed for project collections" : ""}
                >
                  <option value="">Select a supplier *</option>
                  {suppliersList.map((supplier: any) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              {!collection && (
                <button
                  type="button"
                  onClick={() => setShowAddSupplier(!showAddSupplier)}
                  className="text-[13px] text-[#18A5F9] hover:text-[#0D8FDE] font-semibold transition-colors"
                >
                  + Add new supplier
                </button>
              )}
            </div>

            {showAddSupplier && (
              <div className="mt-3 p-3 bg-[#F9FAFB] rounded-xl border border-[#ECEEF1]">
                <div className="space-y-2">
                  <div className="md-outlined-textfield">
                    <input
                      type="text"
                      placeholder="New supplier name"
                      value={newSupplier.name}
                      onChange={(e) => {
                        setNewSupplier({ name: e.target.value });
                        setSupplierError('');
                      }}
                      className="w-full"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSupplier();
                        }
                      }}
                    />
                  </div>

                  {supplierError && (
                    <div className="text-red-700 text-sm">{supplierError}</div>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddSupplier}
                      className="md-btn md-btn-primary text-[13px] px-3 py-1"
                    >
                      Add Supplier
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddSupplier(false);
                        setNewSupplier({ name: '' });
                        setSupplierError('');
                      }}
                      className="md-btn md-btn-outlined text-[13px] px-3 py-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Country *</label>
            <div className="md-outlined-textfield">
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full"
                required
              >
              <option value="">Select a country</option>
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Survey URL *</label>
            <div className="md-outlined-textfield">
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                className="w-full"
                placeholder="https://survey-provider.com/survey/12345"
                required
              />
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1">
              Insert{' '}
              <code className="px-1 py-0.5 bg-[#F3F4F6] rounded text-[#0B7BC7] font-mono">
                {(suppliersList.find((s: any) => s.id === formData.supplierId)?.placeholderToken) || '[RC]'}
              </code>{' '}
              where the respondent's ID needs to be placed in your supplier URL.
            </p>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
              Total Target <span className="text-[#DC2626]">*</span>
            </label>
            <div className="md-outlined-textfield">
              <input
                type="number"
                name="totalTarget"
                value={formData.totalTarget}
                onChange={handleChange}
                className="w-full"
                placeholder="500"
                min="0"
                step="1"
                required
              />
            </div>
          </div>

          {/* Research Defender Settings Section */}
          {(() => {
            // Get selected supplier's RD settings for comparison
            const selectedSupplier = formData.supplierId 
              ? suppliersList.find((s: any) => s.id === formData.supplierId)
              : null;
            
            // Check if current settings differ from supplier defaults
            const hasOverrides = selectedSupplier && (
              formData.rdEnabled !== (selectedSupplier.rdEnabled ?? true) ||
              Number(formData.rdTriggerScore) !== (selectedSupplier.rdTriggerScore ?? 0) ||
              formData.rdBlockDuplicates !== (selectedSupplier.rdBlockDuplicates ?? false)
            );
            
            // Reset handler for RD settings
            const handleResetRdToSupplierDefaults = () => {
              if (selectedSupplier) {
                setFormData(prev => ({
                  ...prev,
                  rdEnabled: selectedSupplier.rdEnabled ?? true,
                  rdTriggerScore: selectedSupplier.rdTriggerScore ?? 0,
                  rdBlockDuplicates: selectedSupplier.rdBlockDuplicates ?? false,
                }));
              }
            };
            
            return (
              <div className="border-t border-[#ECEEF1] pt-4 mt-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-[13px] font-bold text-[#374151] uppercase tracking-wider">Research Defender Settings</h3>
                  {hasOverrides && (
                    <button
                      type="button"
                      onClick={handleResetRdToSupplierDefaults}
                      className="text-xs text-[#18A5F9] hover:text-[#0D8FDE] font-semibold"
                    >
                      Reset to Supplier Defaults
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#9CA3AF] mb-4">
                  {formData.supplierId 
                    ? hasOverrides 
                      ? 'Settings have been modified from supplier defaults.'
                      : 'Using supplier defaults. Modify to override for this collection.'
                    : 'Select a supplier to pre-fill these settings, or configure manually.'}
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center space-x-2 flex-1">
                      <input
                        type="checkbox"
                        name="rdEnabled"
                        checked={formData.rdEnabled}
                        onChange={handleChange}
                        className="rounded border-[#ECEEF1]"
                      />
                      <span className="text-sm font-medium text-[#374151]">Enable Research Defender</span>
                    </label>
                    
                    <label className="flex items-center space-x-2 flex-1">
                      <input
                        type="checkbox"
                        name="rdBlockDuplicates"
                        checked={formData.rdBlockDuplicates}
                        onChange={handleChange}
                        className="rounded border-[#ECEEF1]"
                      />
                      <span className="text-sm font-medium text-[#374151]">Block Duplicates</span>
                    </label>
                    
                    <label className="flex items-center space-x-2 flex-1">
                      <input
                        type="checkbox"
                        name="enableGeoBlocker"
                        checked={formData.enableGeoBlocker}
                        onChange={handleChange}
                        className="rounded border-[#ECEEF1]"
                      />
                      <span className="text-sm font-medium text-[#374151]">Enable Geo Blocker</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#6B7280] mb-2">
                      RD Trigger Score: {formData.rdTriggerScore}
                    </label>
                    <input
                      type="range"
                      name="rdTriggerScore"
                      min="0"
                      max="100"
                      value={formData.rdTriggerScore}
                      onChange={handleChange}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-[#9CA3AF]">
                      <span>0 (Strict)</span>
                      <span className={`font-medium ${
                        formData.rdTriggerScore <= 30 ? 'text-[#EF4444]' :
                        formData.rdTriggerScore <= 60 ? 'text-[#F59E0B]' : 'text-emerald-600'
                      }`}>
                        {formData.rdTriggerScore <= 30 ? 'Strict' : formData.rdTriggerScore <= 60 ? 'Moderate' : 'Lenient'}
                      </span>
                      <span>100 (Lenient)</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Redirect URL Settings Section */}
          {(() => {
            const selectedSupplier = formData.supplierId 
              ? suppliersList.find((s: any) => s.id === formData.supplierId)
              : null;

            const supplierComplete = selectedSupplier?.completeUrlTemplate || '';
            const supplierTerminate = selectedSupplier?.terminateUrlTemplate || '';
            const supplierQuotaFull = selectedSupplier?.quotaFullUrlTemplate || '';
            const supplierSecurity = selectedSupplier?.securityTerminateUrlTemplate || '';

            const hasUrlOverrides = !!(selectedSupplier && (
              formData.completeUrlTemplate !== supplierComplete ||
              formData.terminateUrlTemplate !== supplierTerminate ||
              formData.quotaFullUrlTemplate !== supplierQuotaFull ||
              formData.securityTerminateUrlTemplate !== supplierSecurity
            ));

            const handleResetUrlsToSupplierDefaults = () => {
              if (!selectedSupplier) return;
              setFormData(prev => ({
                ...prev,
                completeUrlTemplate: supplierComplete,
                terminateUrlTemplate: supplierTerminate,
                quotaFullUrlTemplate: supplierQuotaFull,
                securityTerminateUrlTemplate: supplierSecurity,
              }));
            };

            const urlFields: Array<{
              name: 'completeUrlTemplate' | 'terminateUrlTemplate' | 'quotaFullUrlTemplate' | 'securityTerminateUrlTemplate';
              label: string;
              supplierValue: string;
            }> = [
              { name: 'completeUrlTemplate', label: 'Complete URL', supplierValue: supplierComplete },
              { name: 'terminateUrlTemplate', label: 'Terminate URL', supplierValue: supplierTerminate },
              { name: 'quotaFullUrlTemplate', label: 'Quota Full URL', supplierValue: supplierQuotaFull },
              { name: 'securityTerminateUrlTemplate', label: 'Security Terminate URL', supplierValue: supplierSecurity },
            ];

            return (
              <div className="border-t border-[#ECEEF1] pt-4 mt-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-[13px] font-bold text-[#374151] uppercase tracking-wider">Redirect URL Settings</h3>
                  {hasUrlOverrides && (
                    <button
                      type="button"
                      onClick={handleResetUrlsToSupplierDefaults}
                      className="text-xs text-[#18A5F9] hover:text-[#0D8FDE] font-semibold"
                    >
                      Reset to Supplier Defaults
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#9CA3AF] mb-4">
                  {formData.supplierId 
                    ? hasUrlOverrides 
                      ? 'URLs have been modified from supplier defaults.'
                      : 'Using supplier defaults. Modify to override for this collection.'
                    : 'Select a supplier to pre-fill these URLs, or configure manually.'}
                </p>
                
                <div className="space-y-4">
                  {urlFields.map(({ name, label, supplierValue }) => (
                    <div key={name}>
                      <label className="block text-sm font-medium text-[#6B7280] mb-1">
                        {label}
                        {selectedSupplier && !supplierValue && (
                          <span className="ml-2 text-amber-600 font-normal normal-case tracking-normal">
                            Not set on supplier
                          </span>
                        )}
                      </label>
                      <div className="md-outlined-textfield">
                        <input
                          type="text"
                          name={name}
                          value={formData[name]}
                          onChange={handleChange}
                          className="w-full"
                        />
                      </div>
                      <p className="text-xs text-[#9CA3AF] mt-1">Include placeholder for panel ID.</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Quality Screener override */}
          <div className="border-t border-[#ECEEF1] pt-4 mt-4">
            <h3 className="text-[13px] font-bold text-[#374151] uppercase tracking-wider mb-2">
              Quality Screener
            </h3>
            <label className="flex items-start gap-2 mb-3">
              <input
                type="checkbox"
                checked={useCustomScreener}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseCustomScreener(checked);
                  if (!checked) setCollectionScreenerIds([]);
                }}
                className="rounded border-[#ECEEF1] mt-0.5"
              />
              <span className="text-sm font-medium text-[#374151]">
                Use custom screener questions for this collection
              </span>
            </label>
            {useCustomScreener ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                  <p className="text-xs text-amber-800">
                    Project-level quality questions are ignored for this collection while a custom
                    selection is set. Clear the override (uncheck above) to inherit project defaults.
                  </p>
                </div>
                {!screenerLoaded ? (
                  <div className="h-12 md-skeleton rounded-xl" />
                ) : (
                  <QualityQuestionPicker
                    bank={qualityBank}
                    selectedIds={collectionScreenerIds}
                    onToggle={(questionId) => {
                      setCollectionScreenerIds((prev) =>
                        prev.includes(questionId)
                          ? prev.filter((id) => id !== questionId)
                          : [...prev, questionId]
                      );
                    }}
                    onReorder={(draggedId, targetId) => {
                      setCollectionScreenerIds((prev) => {
                        const draggedIndex = prev.indexOf(draggedId);
                        const targetIndex = prev.indexOf(targetId);
                        if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
                          return prev;
                        }
                        const next = [...prev];
                        const [removed] = next.splice(draggedIndex, 1);
                        next.splice(targetIndex, 0, removed);
                        return next;
                      });
                    }}
                    emptyHint={
                      qualityBank.length === 0
                        ? 'No active questions in the bank.'
                        : 'Select at least one question, or turn off the override to use project defaults.'
                    }
                  />
                )}
              </>
            ) : (
              <p className="text-xs text-[#9CA3AF]">
                This collection inherits the project&apos;s quality screener questions.
              </p>
            )}
          </div>

          {error && (
            <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
          )}

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
                {isLoading ? `${collection ? 'Updating' : 'Creating'}...` : `${collection ? 'Update' : 'Create'} Collection`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/** Ordered multi-select for assigning quality questions (project or collection). */
const QualityQuestionPicker = ({
  bank,
  selectedIds,
  onToggle,
  onReorder,
  emptyHint,
}: {
  bank: QualityQuestion[];
  selectedIds: string[];
  onToggle: (questionId: string) => void;
  onReorder: (draggedId: string, targetId: string) => void;
  emptyHint: string;
}) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const byId = new Map(bank.map((q) => [q.id, q]));
  // Include assigned questions that may have been deactivated (still in selectedIds)
  const orderedSelected = selectedIds
    .map((id) => byId.get(id))
    .filter((q): q is QualityQuestion => !!q);

  const unselected = bank.filter((q) => !selectedIds.includes(q.id));

  const handleDragStart = (questionId: string) => {
    setDraggedId(questionId);
  };

  const handleDragOver = (event: React.DragEvent, overId: string) => {
    event.preventDefault();
    if (draggedId && draggedId !== overId) {
      setDragOverId(overId);
    }
  };

  const handleDrop = (event: React.DragEvent, targetId: string) => {
    event.preventDefault();
    if (draggedId && draggedId !== targetId) {
      onReorder(draggedId, targetId);
    }
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  if (bank.length === 0 && selectedIds.length === 0) {
    return (
      <div className="text-sm text-[#9CA3AF] bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-4">
        {emptyHint}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orderedSelected.length > 0 && (
        <div>
          <div className="text-[12px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
            Selected order ({orderedSelected.length})
          </div>
          <div className="space-y-2">
            {orderedSelected.map((q, index) => (
              <div
                key={q.id}
                onDragOver={(e) => handleDragOver(e, q.id)}
                onDrop={(e) => handleDrop(e, q.id)}
                className={`flex items-start gap-3 bg-[#F9FAFB] border rounded-xl p-3 transition-colors ${
                  dragOverId === q.id
                    ? 'border-[#18A5F9] shadow-[0_0_0_1px_#18A5F9]'
                    : 'border-[#ECEEF1]'
                } ${draggedId === q.id ? 'opacity-50' : ''}`}
              >
                <button
                  type="button"
                  draggable
                  onDragStart={() => handleDragStart(q.id)}
                  onDragEnd={handleDragEnd}
                  className="cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#6B7280] shrink-0 mt-0.5"
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  <GripVertical className="w-5 h-5" />
                </button>
                <input
                  type="checkbox"
                  checked
                  onChange={() => onToggle(q.id)}
                  className="mt-1 rounded border-[#ECEEF1]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1A1F2B]">{q.prompt}</div>
                  <div className="text-xs text-[#9CA3AF] mt-0.5">
                    {q.type === 'multiple_choice' ? 'Coded response' : 'Open-ended'} · #{index + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {orderedSelected.length === 0 && (
        <div className="text-sm text-[#9CA3AF] bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-4">
          {emptyHint}
        </div>
      )}

      {unselected.length > 0 && (
        <div>
          <div className="text-[12px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
            Available questions
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {unselected.map((q) => (
              <label
                key={q.id}
                className="flex items-start gap-3 border border-[#ECEEF1] rounded-xl p-3 hover:border-[#B3DCFA] cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => onToggle(q.id)}
                  className="mt-1 rounded border-[#ECEEF1]"
                />
                <div className="min-w-0">
                  <div className="text-sm text-[#374151]">{q.prompt}</div>
                  <div className="text-xs text-[#9CA3AF] mt-0.5">
                    {q.type === 'multiple_choice' ? 'Coded response' : 'Open-ended'}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailPage;
